import type { Dayjs } from 'dayjs'

// types
import type {
  CalendarBlockout,
  CalendarBlockoutBand,
  CalendarEvent,
  CalendarMonthCellLayout,
  CalendarTimeGridItem
} from '@/types/components/calendar'

// utilities
import {
  getEventEnd,
  getEventStart,
  minutesFromDayStart,
  sortCalendarEvents,
  toCalendarDate
} from '@/utilities/calendar/datetime'

/**
 * Options controlling how a day column is laid out.
 */
export interface TimeGridLayoutOptions {
  /** First minute of the day shown in the grid, measured from midnight */
  gridStartMinute: number
  /** Last minute of the day shown in the grid, measured from midnight */
  gridEndMinute: number
  /** Shortest rendered height, so brief events stay tappable */
  minDurationMinutes?: number
  /** Calendar-level fallback timezone */
  timezone?: string
}

/**
 * An event reduced to the minute offsets used by the packing algorithm.
 */
interface PositionedEvent {
  event: CalendarEvent
  startMinute: number
  endMinute: number
}

/**
 * Clamps an event to the visible window and converts it to minute offsets.
 * @param event - Event to position
 * @param dayStart - Start of the day being laid out
 * @param options - Layout options
 * @returns Minute offsets, or null when the event is all-day or entirely outside the window
 */
function positionEvent(event: CalendarEvent, dayStart: Dayjs, options: TimeGridLayoutOptions): PositionedEvent | null {
  // All-day events belong to the separate all-day row
  if (event.allDay) {
    return null
  }

  const start = getEventStart(event, options.timezone)
  const end = getEventEnd(event, options.timezone)

  // Unparseable events are skipped rather than rendered at the top of the grid
  if (!start || !end) {
    return null
  }

  const minDuration = options.minDurationMinutes ?? 15
  const rawStart = minutesFromDayStart(start, dayStart)
  const rawEnd = minutesFromDayStart(end, dayStart)

  const clampedStart = Math.max(rawStart, options.gridStartMinute)
  const clampedEnd = Math.min(rawEnd, options.gridEndMinute)

  // Entirely before or after the visible window
  if (clampedEnd <= options.gridStartMinute || clampedStart >= options.gridEndMinute) {
    return null
  }

  return {
    event,
    startMinute: clampedStart,
    // Enforce a floor so a 5-minute event is still large enough to hit
    endMinute: Math.max(clampedEnd, clampedStart + minDuration)
  }
}

/**
 * Groups positioned events into clusters of transitively overlapping events.
 * A cluster shares a column count, which is what makes side-by-side widths line up.
 * @param positioned - Events sorted by start time
 */
function buildOverlapClusters(positioned: PositionedEvent[]): PositionedEvent[][] {
  const clusters: PositionedEvent[][] = []
  let current: PositionedEvent[] = []
  let clusterEnd = -Infinity

  positioned.forEach(item => {
    // A gap since the furthest end so far closes the cluster
    if (current.length > 0 && item.startMinute >= clusterEnd) {
      clusters.push(current)
      current = []
      clusterEnd = -Infinity
    }

    current.push(item)
    clusterEnd = Math.max(clusterEnd, item.endMinute)
  })

  // Flush the trailing cluster
  if (current.length > 0) {
    clusters.push(current)
  }

  return clusters
}

/**
 * Lays out one day's timed events into side-by-side columns, the way Google Calendar does.
 * Overlapping events are packed greedily into the first free column, and every event in a
 * cluster is given an equal share of the column width.
 * @param events - Events belonging to this day
 * @param dayStart - Start of the day being laid out
 * @param options - Layout options
 * @returns Positioned items with fractional offsets, ready to scale to any pixel height
 */
export function layoutTimeGridEvents(
  events: CalendarEvent[],
  dayStart: Dayjs,
  options: TimeGridLayoutOptions
): CalendarTimeGridItem[] {
  const span = options.gridEndMinute - options.gridStartMinute

  // A zero-height grid cannot position anything
  if (span <= 0) {
    return []
  }

  const positioned = events
    .map(event => positionEvent(event, dayStart, options))
    .filter((item): item is PositionedEvent => item !== null)
    .sort((left, right) => {
      const startDelta = left.startMinute - right.startMinute

      // Equal starts put the longer event first so it takes the leftmost column
      if (startDelta !== 0) {
        return startDelta
      }

      return right.endMinute - left.endMinute
    })

  const items: CalendarTimeGridItem[] = []

  buildOverlapClusters(positioned).forEach(cluster => {
    /** End minute of the last event placed in each column */
    const columnEnds: number[] = []
    /** Column assigned to each event, parallel to `cluster` */
    const assignments: number[] = []

    cluster.forEach(item => {
      const freeColumn = columnEnds.findIndex(end => end <= item.startMinute)

      // Reuse the first column that has already finished, otherwise open a new one
      if (freeColumn === -1) {
        columnEnds.push(item.endMinute)
        assignments.push(columnEnds.length - 1)
        return
      }

      columnEnds[freeColumn] = item.endMinute
      assignments.push(freeColumn)
    })

    const columnCount = columnEnds.length

    cluster.forEach((item, index) => {
      const columnIndex = assignments[index]

      items.push({
        event: item.event,
        startMinute: item.startMinute,
        endMinute: item.endMinute,
        top: (item.startMinute - options.gridStartMinute) / span,
        height: (item.endMinute - item.startMinute) / span,
        left: columnIndex / columnCount,
        width: 1 / columnCount,
        columnIndex,
        columnCount
      })
    })
  })

  return items
}

/**
 * Positions blockouts as background bands behind a day's events.
 * @param blockouts - Blockouts to position
 * @param dayStart - Start of the day being laid out
 * @param options - Layout options
 */
export function layoutBlockoutBands(
  blockouts: CalendarBlockout[],
  dayStart: Dayjs,
  options: TimeGridLayoutOptions
): CalendarBlockoutBand[] {
  const span = options.gridEndMinute - options.gridStartMinute

  // A zero-height grid cannot position anything
  if (span <= 0) {
    return []
  }

  return blockouts.reduce<CalendarBlockoutBand[]>((bands, blockout) => {
    const start = toCalendarDate(blockout.start, options.timezone)
    const end = toCalendarDate(blockout.end, options.timezone)

    // Skip malformed blockouts rather than painting the whole column
    if (!start || !end) {
      return bands
    }

    // All-day blockouts cover the entire visible window of any day they touch
    const rawStart = blockout.allDay ? options.gridStartMinute : minutesFromDayStart(start, dayStart)
    const rawEnd = blockout.allDay ? options.gridEndMinute : minutesFromDayStart(end, dayStart)

    const clampedStart = Math.max(rawStart, options.gridStartMinute)
    const clampedEnd = Math.min(rawEnd, options.gridEndMinute)

    // Entirely outside the visible window
    if (clampedEnd <= clampedStart) {
      return bands
    }

    bands.push({
      blockout,
      top: (clampedStart - options.gridStartMinute) / span,
      height: (clampedEnd - clampedStart) / span
    })

    return bands
  }, [])
}

/**
 * Splits a month cell's events into the ones that fit and the ones behind the "+N more" link.
 * @param events - Events belonging to the cell
 * @param maxVisible - How many chips the cell has room for; zero or less shows all
 * @param timezone - Calendar-level fallback timezone
 */
export function layoutMonthCell(
  events: CalendarEvent[],
  maxVisible: number,
  timezone?: string
): CalendarMonthCellLayout {
  const sorted = sortCalendarEvents(events, timezone)

  // A non-positive limit means the cell is unconstrained
  if (maxVisible <= 0 || sorted.length <= maxVisible) {
    return { visible: sorted, overflow: [], overflowCount: 0 }
  }

  const overflow = sorted.slice(maxVisible)

  return {
    visible: sorted.slice(0, maxVisible),
    overflow,
    overflowCount: overflow.length
  }
}

/**
 * Pixel metrics used to decide how many event chips fit in a month cell.
 */
export interface MonthCellSlotMetrics {
  /** Height of one event chip */
  eventHeight: number
  /** Height of the "+N more" link */
  moreHeight: number
  /** Gap between stacked chips */
  gap: number
}

/**
 * How many event chips fit in `availableHeight` before the overflow link is required.
 * When not every event fits, space is reserved for the "+N more" row.
 * @param availableHeight - Pixels remaining below the date header (and any blockouts)
 * @param eventCount - Events that want to render in the cell
 * @param metrics - Chip and overflow-link sizes
 */
export function countMonthCellSlots(
  availableHeight: number,
  eventCount: number,
  metrics: MonthCellSlotMetrics
): number {
  // Nothing to place, or the cell has not been measured yet
  if (availableHeight <= 0 || eventCount <= 0) {
    return 0
  }

  const { eventHeight, moreHeight, gap } = metrics

  // Degenerate chip size would loop forever
  if (eventHeight <= 0) {
    return 0
  }

  /**
   * Pixel height of `count` chips, plus the overflow row when `withMore` is set.
   * @param count - Visible chips
   * @param withMore - Whether the "+N more" link is shown underneath
   */
  function stackHeight(count: number, withMore: boolean): number {
    // An overflow-only cell is just the more link
    if (count <= 0) {
      return withMore ? moreHeight : 0
    }

    const chips = count * eventHeight + (count - 1) * gap

    return withMore ? chips + gap + moreHeight : chips
  }

  // Everything fits without a more link
  if (stackHeight(eventCount, false) <= availableHeight) {
    return eventCount
  }

  let visible = 0

  // Leave at least one event behind the more link so it has something to show
  while (visible < eventCount - 1 && stackHeight(visible + 1, true) <= availableHeight) {
    visible += 1
  }

  return visible
}

/**
 * Fraction of the visible grid at which the current-time line sits.
 * @param now - Current moment
 * @param dayStart - Start of the day being laid out
 * @param options - Layout options
 * @returns A value between 0 and 1, or null when now falls outside the window
 */
export function getNowIndicatorOffset(now: Dayjs, dayStart: Dayjs, options: TimeGridLayoutOptions): number | null {
  const span = options.gridEndMinute - options.gridStartMinute

  // A zero-height grid cannot position anything
  if (span <= 0) {
    return null
  }

  const minute = minutesFromDayStart(now, dayStart)

  // Outside the visible hours, so the caller should hide the indicator
  if (minute < options.gridStartMinute || minute > options.gridEndMinute) {
    return null
  }

  return (minute - options.gridStartMinute) / span
}
